import { isValidPlayerId } from '../security/input.js';

const OPEN_KEY = 'dechess-challenge-open-v1';
const BLOCKED_KEY = 'dechess-challenge-blocked-v1';
const MAX_BLOCKED = 100;

function readJson(key) {
  try {
    return JSON.parse(localStorage.getItem(key)) || {};
  } catch {
    return {};
  }
}

function writeJson(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function loadOpenToChallenges(playerId, defaultValue = true) {
  const stored = readJson(OPEN_KEY)[playerId];
  return stored === undefined ? defaultValue : Boolean(stored);
}

export function saveOpenToChallenges(playerId, open) {
  const store = readJson(OPEN_KEY);
  store[playerId] = Boolean(open);
  writeJson(OPEN_KEY, store);
}

function normalizeBlockedEntry(entry) {
  if (!entry) {
    return null;
  }
  if (typeof entry === 'string') {
    const playerId = String(entry).toLowerCase();
    return isValidPlayerId(playerId)
      ? { playerId, nickname: '', blockedAt: 0 }
      : null;
  }
  const playerId = String(entry.playerId || '').toLowerCase();
  if (!isValidPlayerId(playerId)) {
    return null;
  }
  return {
    playerId,
    nickname: String(entry.nickname || '').trim().slice(0, 32),
    blockedAt: Number(entry.blockedAt) || 0
  };
}

export function loadBlockedChallengers(playerId) {
  if (!playerId) {
    return [];
  }
  const raw = readJson(BLOCKED_KEY)[playerId];
  if (!Array.isArray(raw)) {
    return [];
  }
  const seen = new Set();
  const entries = [];
  for (const item of raw) {
    const entry = normalizeBlockedEntry(item);
    if (!entry || seen.has(entry.playerId)) {
      continue;
    }
    seen.add(entry.playerId);
    entries.push(entry);
  }
  return entries
    .sort((left, right) => (right.blockedAt || 0) - (left.blockedAt || 0))
    .slice(0, MAX_BLOCKED);
}

export function saveBlockedChallengers(playerId, entries = []) {
  if (!playerId) {
    return [];
  }
  const store = readJson(BLOCKED_KEY);
  const normalized = [];
  const seen = new Set();
  for (const item of entries) {
    const entry = normalizeBlockedEntry(item);
    if (!entry || seen.has(entry.playerId) || entry.playerId === String(playerId).toLowerCase()) {
      continue;
    }
    seen.add(entry.playerId);
    normalized.push(entry);
  }
  store[playerId] = normalized
    .sort((left, right) => (right.blockedAt || 0) - (left.blockedAt || 0))
    .slice(0, MAX_BLOCKED);
  writeJson(BLOCKED_KEY, store);
  return store[playerId];
}

export function isChallengerBlocked(playerId, challengerPlayerId) {
  const challengerId = String(challengerPlayerId || '').toLowerCase();
  if (!challengerId) {
    return false;
  }
  return loadBlockedChallengers(playerId).some((entry) => entry.playerId === challengerId);
}

export function blockChallenger(playerId, challengerPlayerId, {
  nickname = '',
  blockedAt = Date.now()
} = {}) {
  const entry = normalizeBlockedEntry({
    playerId: challengerPlayerId,
    nickname,
    blockedAt
  });
  if (!entry || entry.playerId === String(playerId || '').toLowerCase()) {
    return loadBlockedChallengers(playerId);
  }
  const existing = loadBlockedChallengers(playerId).filter(
    (item) => item.playerId !== entry.playerId
  );
  return saveBlockedChallengers(playerId, [{ ...entry, blockedAt }, ...existing]);
}

export function unblockChallenger(playerId, challengerPlayerId) {
  const challengerId = String(challengerPlayerId || '').toLowerCase();
  return saveBlockedChallengers(
    playerId,
    loadBlockedChallengers(playerId).filter((entry) => entry.playerId !== challengerId)
  );
}
